import { apiRequest } from './client.js';

export type CaptureStatus = 'pending' | 'submitted' | 'promoted' | 'expired' | 'revoked';

export interface CaptureTokenSummary {
  id: string;
  template_id: string;
  template_version: number;
  client_email: string | null;
  expires_at: string;
  revoked_at: string | null;
  created_at: string;
  submitted_at: string | null;
  promoted_assessment_id: string | null;
  status: CaptureStatus;
}

export interface CaptureTokenCreated {
  id: string;
  token: string;
  capture_url: string;
  expires_at: string;
  created_at: string;
  template_id: string;
  template_version: number;
}

export interface CaptureViewPublic {
  site: { name: string; client_name: string };
  template: { code: string; version: number; name: string; schema: unknown };
  already_submitted: boolean;
}

/**
 * Respuesta del submit (ADR-041 D2): el backend calcula el score al enviar
 * y señaliza si hay resultado disponible para redirigir al cliente.
 */
export interface SubmitCaptureResult {
  ok: boolean;
  /** true cuando el backend pudo calcular el snapshot de score (D1). */
  hasResult: boolean;
}

/**
 * Resultado de la autoevaluacion del cliente — ADR-041 D2.
 * Expone solo datos del propio token; sin notas de auditor ni IDs internos.
 */
export interface CaptureResultView {
  submitted: boolean;
  submitted_at: string;
  global_score: number | null;
  per_domain: Record<string, number>;
  /**
   * true cuando la plantilla no tiene scoring.rules (Fase A).
   * La UI debe rotular el score como informativo, no como veredicto.
   */
  informative_only: boolean;
  /** Banda de madurez derivada del score. Null si informative_only y sin reglas. */
  level: {
    band: string;
    label: string;
    description: string;
  } | null;
  findings: {
    question_text: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }[];
  site: { name: string; client_name: string };
  template: { name: string };
}

/** Resultado del endpoint POST promote (ADR-040 D4). */
export interface PromoteResult {
  assessment_id: string;
  /** UUID de la instancia AUDITOR (in_progress, editable). Null si no hay gemelo auditor. */
  auditor_instance_id: string | null;
  /** 'applied' → prefill ejecutado; 'skipped_no_twin' → sin plantilla auditor gemela. */
  auditor_prefill: 'applied' | 'skipped_no_twin';
  /** Cantidad de campos prellenados desde la respuesta del cliente. */
  prefilled_count: number;
}

export const captureAdminApi = {
  list: (siteId: string) =>
    apiRequest<{ tokens: CaptureTokenSummary[] }>(`/api/v1/sites/${siteId}/capture-tokens`),
  create: (
    siteId: string,
    input: { template_id: string; expires_in_days?: number; client_email?: string },
  ) =>
    apiRequest<CaptureTokenCreated>(`/api/v1/sites/${siteId}/capture-tokens`, {
      method: 'POST',
      body: input,
    }),
  revoke: (siteId: string, tokenId: string) =>
    apiRequest<void>(`/api/v1/sites/${siteId}/capture-tokens/${tokenId}`, {
      method: 'DELETE',
    }),
  promote: (siteId: string, tokenId: string) =>
    apiRequest<PromoteResult>(`/api/v1/sites/${siteId}/capture-tokens/${tokenId}/promote`, {
      method: 'POST',
    }),
};

/** Fetch publico — sin Authorization. */
export async function fetchCaptureView(token: string): Promise<CaptureViewPublic> {
  const res = await fetch(`/api/v1/capture/${encodeURIComponent(token)}`, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const code = body.error ?? `http_${String(res.status)}`;
    throw new Error(`${String(res.status)}:${code}`);
  }
  return (await res.json()) as CaptureViewPublic;
}

/**
 * Envia las respuestas del cliente y devuelve si hay resultado calculado (ADR-041 D2).
 * La firma anterior devolvía void; ahora devuelve SubmitCaptureResult para que
 * Capture.tsx pueda redirigir a /resultado cuando hasResult===true.
 */
export async function submitCaptureResponses(
  token: string,
  responses: Record<string, unknown>,
): Promise<SubmitCaptureResult> {
  const res = await fetch(`/api/v1/capture/${encodeURIComponent(token)}/submit`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ responses }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const code = body.error ?? `http_${String(res.status)}`;
    throw new Error(`${String(res.status)}:${code}`);
  }
  // El backend puede devolver { ok: true } (sin hasResult) en deploys previos.
  // Tratamos la ausencia de hasResult como false para compatibilidad retrograda.
  const body = (await res.json().catch(() => ({}))) as Partial<SubmitCaptureResult>;
  return { ok: body.ok ?? true, hasResult: body.hasResult ?? false };
}

// ─────────────────────────────────────────────────────────────────────
// Sprint II · ADR-042 D4 — Descarga del reporte PDF de autoevaluación
// ─────────────────────────────────────────────────────────────────────

/** Resultado discriminado de la llamada al endpoint de descarga de reporte. */
export type CaptureReportFetchResult = { status: 'ready'; blob: Blob } | { status: 'generating' };

/**
 * Intenta descargar el PDF de autoevaluación del cliente.
 *
 * - 200 → { status: 'ready', blob } — el PDF está listo.
 * - 202 → { status: 'generating' } — el worker aún está procesando.
 * - 409 not_submitted | report_unavailable → lanza Error tipado.
 * - 401/410/400 → lanza Error tipado (token inválido, expirado, revocado).
 */
export async function fetchCaptureReport(token: string): Promise<CaptureReportFetchResult> {
  const res = await fetch(`/api/v1/capture/${encodeURIComponent(token)}/report`, {
    credentials: 'omit',
    headers: { Accept: 'application/pdf, application/json' },
  });

  if (res.status === 202) {
    return { status: 'generating' };
  }

  if (res.ok) {
    const blob = await res.blob();
    return { status: 'ready', blob };
  }

  // Error — extrae el código de error del cuerpo JSON si está disponible.
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  const code = body.error ?? `http_${String(res.status)}`;
  throw new Error(`${String(res.status)}:${code}`);
}

/** Fetch publico del resultado de la autoevaluacion (ADR-041 D2). */
export async function fetchCaptureResult(token: string): Promise<CaptureResultView> {
  const res = await fetch(`/api/v1/capture/${encodeURIComponent(token)}/result`, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const code = body.error ?? `http_${String(res.status)}`;
    throw new Error(`${String(res.status)}:${code}`);
  }
  return (await res.json()) as CaptureResultView;
}
